import React, { useState, useEffect, useRef } from "react";
import BuyButton from './BuyButton'; // adjust path as necessary
import { useSEO } from './useSEO';


function Home() {
  const [level, setLevel] = useState(null);
  const [subject, setSubject] = useState(null);
  const [subjectsList, setSubjectsList] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectsError, setSubjectsError] = useState(null);

  const [files, setFiles] = useState({});
  const [resources, setResources] = useState([]);
  const [board, setBoard] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');



  const [categoriesList, setCategoriesList] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);

  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const buildUrl = (path) => `${API_BASE}${path}`;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I find GCSE or A-Level past papers?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Choose a level, pick your subject and exam board, then open the Past Papers section to view and download papers and mark schemes."
        }
      },
      {
        "@type": "Question",
        "name": "Do you have topic questions and mark schemes?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Each subject has Questions by Topic folders with matching mark schemes so you can practice and self-mark."
        }
      },
      {
        "@type": "Question",
        "name": "Are resources available for AQA, Edexcel, and OCR?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We list resources by exam board; pick your level and subject to see which boards have available papers and topic packs."
        }
      }
    ]
  };

  useSEO({
    title: 'Resource Library',
    description: 'GCSE and A-Level revision resources: past papers, mark schemes, topic questions, and printable practice for AQA, Edexcel, and OCR.',
    canonicalPath: '/',
    schema: [faqSchema]
  });

  // Subject scroller ref and chevron visibility
  const subjectsRef = useRef(null);
  const [showLeftChev, setShowLeftChev] = useState(false);
  const [showRightChev, setShowRightChev] = useState(false);

  const scrollSubjects = (dir) => {
    const el = subjectsRef.current;
    if (!el) return;
    const delta = Math.max(160, Math.round(el.clientWidth * 0.6));
    el.scrollBy({ left: dir === 'left' ? -delta : delta, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = subjectsRef.current;
    if (!el) return;
    const update = () => {
      setShowLeftChev(el.scrollLeft > 8);
      setShowRightChev(el.scrollWidth > el.clientWidth + el.scrollLeft + 8);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    // re-run when level/subject/subjectsList changes so visibility recalculates
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [level, subject, subjectsList]);

  // Fetch subjects dynamically when a level is selected
  // helper to fetch subjects with timeout + retry
  const fetchSubjects = (lvl) => {
    if (!lvl) return;
    setSubjectsLoading(true);
    setSubjectsError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 7000); // 7s timeout
    const safeLevel = encodeURIComponent(lvl);

    fetch(buildUrl(`/api/subjects/${safeLevel}`), { signal: controller.signal })
      .then(res => {
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error(`Server error ${res.status}`);
        return res.json();
      })
      .then(data => {
        setSubjectsList(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('Failed to fetch subjects:', err);
        if (err.name === 'AbortError') {
          setSubjectsError('Request timed out. Please try again.');
        } else {
          setSubjectsError(err.message || 'Failed to fetch subjects');
        }
        setSubjectsList([]);
      })
      .finally(() => setSubjectsLoading(false));
  };

  useEffect(() => {
    if (!level) {
      setSubjectsList([]);
      setSubjectsError(null);
      return;
    }

    fetchSubjects(level);
  }, [level]);


  

  // subjects list is fetched dynamically from the backend (based on /resources/<level>/ folders)

  const boards = ["AQA", "Edexcel", "OCR"];

  // Only show boards that actually have folders for the selected level/subject
  const [availableBoards, setAvailableBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [boardsError, setBoardsError] = useState(null);

  useEffect(() => {
    // Reset when there's no level/subject
    if (!level || !subject) {
      setAvailableBoards([]);
      setBoardsError(null);
      setBoardsLoading(false);
      return;
    }

    setBoardsLoading(true);
    setBoardsError(null);

    const safeLevel = encodeURIComponent(level);
    const safeSubject = encodeURIComponent(subject);
    let cancelled = false;

    // Helper to fetch with timeout
    const fetchWithTimeout = (url, timeout = 5000) => {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeout);
      return fetch(url, { signal: controller.signal })
        .finally(() => clearTimeout(id));
    };

    const checks = boards.map(b => {
      const safeBoard = encodeURIComponent(b);
      return fetchWithTimeout(buildUrl(`/api/categories/${safeLevel}/${safeSubject}/${safeBoard}`), 5000)
        .then(res => res.ok ? res.json() : [])
        .then(data => ({ board: b, has: Array.isArray(data) && data.length > 0 }))
        .catch(err => {
          // Treat aborted or failed fetch as no data for that board
          console.error('Failed to check board availability for', b, err?.name || err);
          return { board: b, has: false };
        });
    });

    // Safety timer to avoid stuck loading state
    const safetyTimer = setTimeout(() => {
      if (!cancelled) {
        console.warn('Board availability check timed out - clearing loading state');
        setBoardsLoading(false);
        setBoardsError(prev => prev || 'Board check timed out');
      }
    }, 6000);

    // Use allSettled so one failing request doesn't block others
    Promise.allSettled(checks)
      .then(results => {
        if (cancelled) return;
        const boardsAvailable = results
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value)
          .filter(Boolean)
          .filter(r => r.has)
          .map(r => r.board);
        console.log('Available boards:', boardsAvailable);
        setAvailableBoards(boardsAvailable);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Error checking boards:', err);
        setBoardsError('Failed to check available boards');
        setAvailableBoards([]);
      })
      .finally(() => {
        if (!cancelled) setBoardsLoading(false);
        clearTimeout(safetyTimer);
      });

    return () => { cancelled = true; clearTimeout(safetyTimer); };
  }, [level, subject]);

  // Page background is controlled by CSS variables (see :root and [data-theme] rules in App.css).
  // The body background will switch between light and dark modes; individual subject backgrounds are no longer used for the page.


  const subjectColors = {
    gcse: {
      Maths: "#A2C4F2",       // light blue
      Physics: "#D7A2F2",     // light purple
      Biology: "#A2F2A2",     // light green
      Chemistry: "#F2C4A2",   // light orange-red
      English: "#FFFACD"      // light yellow
    },
    "a-level": {
      Maths: "#003366",       // dark blue
      Physics: "#5D3FD3",     // deep purple
      Biology: "#006400",     // dark green
      Chemistry: "#B22222",   // firebrick red
      English: "#FFD700"      // golden yellow
    }
  };
  

  // When a board is chosen, fetch available categories and their files
  useEffect(() => {
    if (level && subject && board) {
      setCategoriesError(null);
      setCategoriesLoading(true);
      setFetchError(null);
      setLoadingFiles(true);

      const safeLevel = encodeURIComponent(level);
      const safeSubject = encodeURIComponent(subject);
      const safeBoard = encodeURIComponent(board);

      // Fetch dynamic categories (folder names)
      fetch(buildUrl(`/api/categories/${safeLevel}/${safeSubject}/${safeBoard}`))
        .then(res => {
          if (!res.ok) throw new Error(`Server error ${res.status}`);
          return res.json();
        })
        .then(catData => {
          setCategoriesList(Array.isArray(catData) ? catData : []);
          // Reset selected category if it's not present
          setSelectedCategory(prev => (prev === 'All' || (Array.isArray(catData) && catData.includes(prev)) ? prev : 'All'));
        })
        .catch(err => {
          console.error('Failed to fetch categories:', err);
          setCategoriesError(err.message || 'Failed to fetch categories');
          setCategoriesList([]);
        })
        .finally(() => setCategoriesLoading(false));

      // Fetch files for all categories (dynamic)
      fetch(buildUrl(`/api/resources/${safeLevel}/${safeSubject}/${safeBoard}`))
        .then(res => {
          if (!res.ok) throw new Error(`Server error ${res.status}`);
          return res.json();
        })
        .then(data => {
          // data is a mapping { categoryName: { files: [...], subfolders: {...} } }
          setFiles(data || {});
        })
        .catch(err => {
          console.error(`Failed to fetch resources:`, err);
          setFetchError(err.message || 'Failed to fetch resources');
          setFiles({});
        })
        .finally(() => setLoadingFiles(false));
    }
  }, [level, subject, board]);



    
  

  const renderSection = (title) => {
    const catData = files[title];
    // backward compatibility: if catData is array (old format), treat as files
    const directFiles = Array.isArray(catData) ? catData : (catData && Array.isArray(catData.files) ? catData.files : []);
    const subfolders = catData && !Array.isArray(catData) && catData.subfolders ? catData.subfolders : {};

    return (
      <details className="resource-section" open>
        <summary>
          <div className="resource-title">{title}</div>
          <div className="resource-count">{(directFiles.length + Object.values(subfolders).reduce((s, a) => s + (Array.isArray(a) ? a.length : 0), 0)) ? `(${directFiles.length + Object.values(subfolders).reduce((s, a) => s + (Array.isArray(a) ? a.length : 0), 0)})` : ''}</div>
          <div style={{ marginLeft: 'auto' }}>
            <a
              className="section-buy"
              href={`/payment?level=${encodeURIComponent(level)}&subject=${encodeURIComponent(subject)}&board=${encodeURIComponent(board || '')}&category=${encodeURIComponent(title)}`}
              aria-label={`Buy ${title} for ${subject} ${level} ${board || ''}`}
            >Buy</a>
          </div>
        </summary>

        {loadingFiles ? (
          <div aria-busy="true" aria-live="polite" className="skeleton-list">
            <div className="skeleton-line" style={{ width: '80%' }} />
            <div className="skeleton-line" style={{ width: '60%' }} />
            <div className="skeleton-line" style={{ width: '70%' }} />
          </div>
        ) : fetchError ? (
          <p style={{ color: 'red' }}>Could not load resources: {fetchError}</p>
        ) : (
          <div>
            <ul>
              {directFiles.length > 0 && directFiles.map((file, index) => {
                const ext = file.includes('.') ? file.split('.').pop().toUpperCase() : '';
                const fileUrl = buildUrl(`/files/${encodeURIComponent(level)}/${encodeURIComponent(subject)}/${encodeURIComponent(board)}/${encodeURIComponent(title)}/${encodeURIComponent(file)}`);
                return (
                  <li key={"f-"+index} className="file-row">
                    <a className="file-link" href={fileUrl} target="_blank" rel="noopener noreferrer">
                      {file}
                    </a>
                    <span className="file-type">{ext}</span>
                    <a className="download-btn" href={fileUrl} download aria-label={`Download ${file}`}>
                      <svg className="download-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                        <path d="M12 3v13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </a>
                  </li>
                );
              })}

              {Object.keys(subfolders).length > 0 && Object.keys(subfolders).map(sub => {
                const subFiles = Array.isArray(subfolders[sub]) ? subfolders[sub] : [];
                return (
                  <React.Fragment key={"frag-"+sub}>
                    <li key={"s-"+sub} className="subheading-row">
                      <div className="subheading-title"><span className="sub-icon" aria-hidden>📁</span><span className="sub-title-text">{sub}</span><span className="sub-count-pill">{subFiles.length}</span></div>
                    </li>

                    <li key={"sf-wrap-"+sub} className="subfiles-wrapper">
                      <ul className={`subfiles open`}>
                        {subFiles.length > 0 ? subFiles.map((file, idx) => {
                          const ext = file.includes('.') ? file.split('.').pop().toUpperCase() : '';
                          const fileUrl = buildUrl(`/files/${encodeURIComponent(level)}/${encodeURIComponent(subject)}/${encodeURIComponent(board)}/${encodeURIComponent(title)}/${encodeURIComponent(sub)}/${encodeURIComponent(file)}`);
                          return (
                            <li key={"sf-"+idx} className="file-row subfile-row">
                              <a className="file-link" href={fileUrl} target="_blank" rel="noopener noreferrer">
                                {file}
                              </a>
                              <span className="file-type">{ext}</span>
                              <a className="download-btn" href={fileUrl} download aria-label={`Download ${file}`}>
                                <svg className="download-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                  <path d="M12 3v13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </a>
                            </li>
                          );
                        }) : <li className="subfile-empty">No resources in this section.</li>}
                      </ul>
                    </li>
                  </React.Fragment>
                );
              })}

              {directFiles.length === 0 && Object.keys(subfolders).length === 0 && <li>No resources available.</li>}
            </ul>
          </div>
        )}

      </details>
    );
  };

  

  return (
    <div
      className="app"
      style={{
        transition: "background-color 0.8s ease-in-out",
        minHeight: "100vh"
      }}>
      <header className="hero">
        <h1>Welcome to the Resource Hub 📚</h1>
        <p>Find past papers, assessments, and more.</p>
      </header>

      <div className="levels" role="tablist" aria-label="Education level">
        <button className={`level ${level === 'gcse' ? 'active' : ''}`} onClick={() => { setLevel('gcse'); setSubject(null); setResources({}); setBoard(null); setSelectedCategory('All'); }} aria-pressed={level === 'gcse'}>GCSE</button>
        <button className={`level ${level === 'a-level' ? 'active' : ''}`} onClick={() => { setLevel('a-level'); setSubject(null); setResources({}); setBoard(null); setSelectedCategory('All'); }} aria-pressed={level === 'a-level'}>A-Level</button>
      </div>

      {level && (
        <div className="subjects-wrap" style={{ position: 'relative' }}>
        <button
          className={`chev left ${showLeftChev ? 'visible' : ''}`}
          onClick={() => scrollSubjects('left')}
          aria-hidden={!showLeftChev}
          aria-label="Scroll subjects left"
        >‹</button>

          <div className="subjects" ref={subjectsRef}>
          {subjectsLoading ? (
            <div style={{ display: 'flex', gap: 12, padding: '6px 8px' }}>
              <div className="skeleton-line" style={{ width: 120, height: 36 }} />
              <div className="skeleton-line" style={{ width: 100, height: 36 }} />
              <div className="skeleton-line" style={{ width: 140, height: 36 }} />
            </div>
          ) : subjectsError ? (
            <div style={{ color: 'red', display: 'flex', gap: 12, alignItems: 'center' }}>
              <div>Failed to load subjects: {subjectsError}</div>
              <button onClick={() => fetchSubjects(level)} style={{ background: '#1d3557', color: '#fff', padding: '6px 10px', borderRadius: 8 }}>Retry</button>
            </div>
          ) : (
            subjectsList.map((subj) => (
              <button
                key={subj}
                className={`level subject-button ${level} ${subj} ${subject === subj ? 'active' : ''}`}
                onClick={() => { setSubject(subj); setBoard(null); setSelectedCategory('All'); }}
                aria-pressed={subject === subj}

              >
                {subj}
              </button>
            ))
          )}
        </div>

        <button
          className={`chev right ${showRightChev ? 'visible' : ''}`}
          onClick={() => scrollSubjects('right')}
          aria-hidden={!showRightChev}
          aria-label="Scroll subjects right"
        >›</button>
      </div> 
      )}

      {subject && (
        <div className="exam-boards" style={{ marginTop: "1rem", display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap" }} aria-live="polite">
          {boardsLoading ? (
            <div style={{ color: '#666' }}>Checking available boards...</div>
          ) : boardsError ? (
            <div style={{ color: 'red' }}>Failed to check boards</div>
          ) : availableBoards.length === 0 ? (
            <div style={{ color: '#666' }}>No exam boards available for this subject yet.</div>
          ) : (
            availableBoards.map((b) => (
              <button
                key={b}
                className={`board-chip ${board === b ? 'active' : ''}`}
                onClick={() => setBoard(b)}
                aria-pressed={board === b}
              >
                {b}
              </button>
            ))
          )}
        </div>
      )}

      {subject && (
        <div style={{ marginTop: "3rem" }}>
          <h2 style={{ textAlign: "center" }}>{level.toUpperCase()} - {subject}</h2>

          {!board ? (
            <p style={{ textAlign: 'center', marginTop: '1rem', color: '#333' }}>Please select an exam board above to view resources.</p>
          ) : (
            <>
              {/* <BuyButton level={level} subject={subject} board={board} /> */}
              <hr></hr>

              <div className="category-chips" role="tablist" aria-label="Resource categories">
                <button className={`category-chip ${selectedCategory === 'All' ? 'active' : ''}`} onClick={() => setSelectedCategory('All')} aria-pressed={selectedCategory === 'All'}>All</button>
                {categoriesLoading ? (
                  <div style={{ display: 'inline-flex', gap: 8 }}>
                    <div className="skeleton-line" style={{ width: 80, height: 28 }} />
                    <div className="skeleton-line" style={{ width: 70, height: 28 }} />
                  </div>
                ) : categoriesError ? (
                  <div style={{ color: 'red' }}>Failed to load categories</div>
                ) : (
                  categoriesList.map(cat => (
                    <button key={cat} className={`category-chip ${selectedCategory === cat ? 'active' : ''}`} onClick={() => setSelectedCategory(cat)} aria-pressed={selectedCategory === cat}>{cat}</button>
                  ))
                )}
              </div>

              <div className="resource-grid" aria-hidden={!board}>
                {(selectedCategory === 'All' ? (categoriesList.length ? categoriesList : Object.keys(files)) : [selectedCategory]).map(cat => renderSection(cat))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Home;
